import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { onMaintenance } from '../../services/api';

/**
 * Full-width strip shown while the system is in a global write freeze.
 *
 * The server blocks writes and returns 503 { code: 'MAINTENANCE' }; reads keep working, so the
 * panel behind this strip still renders normally. That is deliberate — staff can keep looking
 * things up during a cutover window, they just cannot change anything.
 *
 * State comes from the api.js interceptor rather than a poll: the first blocked write raises it,
 * and the first write that succeeds afterwards clears it. No extra requests, and no need for
 * every page to know maintenance mode exists.
 */
export default function MaintenanceBanner() {
  const [state, setState] = useState({ active: false, message: '', started_at: null });
  const [elapsed, setElapsed] = useState('');

  useEffect(() => onMaintenance(setState), []);

  useEffect(() => {
    if (!state.active || !state.started_at) { setElapsed(''); return undefined; }
    const tick = () => {
      const mins = Math.max(0, Math.round((Date.now() - new Date(state.started_at)) / 60000));
      setElapsed(mins < 1 ? 'just started' : `${mins} min so far`);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [state.active, state.started_at]);

  if (!state.active) return null;

  return (
    <div style={s.strip} role="status" aria-live="polite">
      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
      <span style={s.msg}>{state.message}</span>
      <span style={s.sub}>
        You can still view everything — changes are paused{elapsed ? ` · ${elapsed}` : ''}.
      </span>
    </div>
  );
}

const s = {
  strip: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    padding: '10px 18px',
    background: '#FEF3C7',
    borderBottom: '1px solid #FCD34D',
    color: '#78350F',
    fontSize: 14,
    lineHeight: 1.4,
  },
  msg: { fontWeight: 600 },
  sub: { opacity: 0.85, fontSize: 13 },
};
